import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSupportSession } from '../contexts/SupportSessionContext';
import { Monitor, X, Check, AlertTriangle, Loader2, RefreshCw, Wifi, WifiOff, VideoOff, MousePointer2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';

/**
 * Banner shown when an admin requests screen sharing.
 * Shows consent dialog and active sharing indicator.
 * Includes cursor overlay feature for admin guidance.
 */
export const SupportSessionBanner = () => {
  const {
    pendingSession,
    activeSession,
    isSharing,
    connectionStatus,
    error,
    loading,
    cursorEnabled,
    acceptSession,
    declineSession,
    startSharing,
    stopSharing,
    retryConnection,
    toggleCursor,
    onCursorPosition,
  } = useSupportSession();
  
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showActiveDialog, setShowActiveDialog] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0, visible: false });
  
  // Show consent dialog when there's a pending session
  useEffect(() => {
    if (pendingSession && !activeSession) {
      setShowConsentDialog(true);
    }
  }, [pendingSession, activeSession]);
  
  // Reset cursor state when session ends
  useEffect(() => {
    if (!isSharing) {
      setCursorPosition({ x: 0, y: 0, visible: false });
    }
  }, [isSharing]);
  
  // Subscribe to cursor position updates
  useEffect(() => {
    if (!isSharing || !cursorEnabled) {
      setCursorPosition({ x: 0, y: 0, visible: false });
      return;
    }
    
    const unsubscribe = onCursorPosition((pos) => {
      setCursorPosition(pos);
    });
    
    return unsubscribe;
  }, [isSharing, cursorEnabled, onCursorPosition]);
  
  const handleAccept = async () => {
    const result = await acceptSession(pendingSession.id);
    if (result.success) {
      setShowConsentDialog(false);
      // Automatically start sharing after accepting
      await startSharing();
    }
  };
  
  const handleDecline = async () => {
    await declineSession(pendingSession.id);
    setShowConsentDialog(false);
  };
  
  const handleStop = async () => {
    await stopSharing();
    setShowActiveDialog(false);
  };
  
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'failed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'failed':
        return 'Connection Failed';
      default:
        return 'Disconnected';
    }
  };
  
  // Active sharing banner
  if (activeSession && isSharing) {
    return (
      <>
        {/* Cursor overlay - shown when cursor is enabled and visible */}
        {cursorEnabled && cursorPosition.visible && (
          <div 
            className="fixed z-[9999] pointer-events-none"
            style={{
              left: `${cursorPosition.x * 100}vw`,
              top: `${cursorPosition.y * 100}vh`,
              transform: 'translate(-50%, -50%)'
            }}
          >
            <div className="relative">
              {/* Cursor indicator */}
              <div className="w-6 h-6 rounded-full bg-blue-500/30 border-2 border-blue-500 animate-pulse" />
              {/* Cursor dot */}
              <div className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-blue-600 transform -translate-x-1/2 -translate-y-1/2" />
              {/* Label */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded whitespace-nowrap">
                Support cursor
              </div>
            </div>
          </div>
        )}
        
        {/* Persistent sharing indicator */}
        <div 
          className="sticky top-0 z-[100] px-4 py-2 flex items-center justify-between gap-4 shadow-lg bg-red-600 text-white cursor-pointer"
          onClick={() => setShowActiveDialog(true)}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              <span className="font-bold text-sm uppercase tracking-wide">
                Screen Sharing Active
              </span>
            </div>
            
            <div className="h-5 w-px bg-current opacity-30" />
            
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${getConnectionStatusColor()} animate-pulse`} />
              <span className="text-sm">
                {getConnectionStatusText()}
              </span>
            </div>
            
            <div className="h-5 w-px bg-current opacity-30" />
            
            {/* Not recorded indicator */}
            <Badge variant="outline" className="text-xs bg-red-700/50 text-white border-red-400/50">
              <VideoOff className="h-3 w-3 mr-1" />
              Not recorded
            </Badge>
            
            {/* Cursor toggle */}
            {connectionStatus === 'connected' && (
              <>
                <div className="h-5 w-px bg-current opacity-30" />
                <div 
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCursor();
                  }}
                >
                  <Switch 
                    checked={cursorEnabled}
                    className="data-[state=checked]:bg-blue-500"
                  />
                  <span className="text-xs flex items-center gap-1">
                    <MousePointer2 className="h-3 w-3" />
                    Cursor
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs opacity-75">
              Click to manage
            </span>
            
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleStop();
              }}
              size="sm"
              variant="secondary"
              className="bg-red-700 hover:bg-red-800 text-white border-red-500"
            >
              <X className="h-3.5 w-3.5 mr-1.5" />
              Stop Sharing
            </Button>
          </div>
        </div>
        
        {/* Active session details dialog */}
        <Dialog open={showActiveDialog} onOpenChange={setShowActiveDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-red-600" />
                Screen Sharing Session
              </DialogTitle>
              <DialogDescription>
                A support admin is viewing your Stock & Load browser tab.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Connection status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium">Connection Status</span>
                <div className="flex items-center gap-2">
                  {connectionStatus === 'connected' ? (
                    <Wifi className="h-4 w-4 text-green-600" />
                  ) : connectionStatus === 'failed' ? (
                    <WifiOff className="h-4 w-4 text-red-600" />
                  ) : (
                    <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
                  )}
                  <span className={`text-sm font-medium ${
                    connectionStatus === 'connected' ? 'text-green-600' :
                    connectionStatus === 'failed' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {getConnectionStatusText()}
                  </span>
                </div>
              </div>
              
              {/* Cursor overlay toggle */}
              {connectionStatus === 'connected' && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MousePointer2 className="h-4 w-4 text-blue-600" />
                    <div>
                      <span className="text-sm font-medium">Show support cursor</span>
                      <p className="text-xs text-gray-500">Let admin guide you with their cursor</p>
                    </div>
                  </div>
                  <Switch 
                    checked={cursorEnabled}
                    onCheckedChange={toggleCursor}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>
              )}
              
              {/* Error message */}
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Failed connection retry */}
              {connectionStatus === 'failed' && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    The connection couldn't be established. This may be due to your network's firewall or NAT settings.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={retryConnection} variant="outline" size="sm">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                    <Button onClick={handleStop} variant="ghost" size="sm" className="text-red-600">
                      Cancel Session
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Privacy notice */}
              <Alert className="border-blue-200 bg-blue-50">
                <Monitor className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 text-sm">
                  <strong>Privacy:</strong> Only this browser tab is being shared. 
                  The admin cannot see other tabs, windows, or your desktop.
                </AlertDescription>
              </Alert>
              
              {/* Not recorded notice */}
              <Alert className="border-gray-200 bg-gray-50">
                <VideoOff className="h-4 w-4 text-gray-600" />
                <AlertDescription className="text-gray-700 text-sm">
                  <strong>Not recorded:</strong> This session is not recorded by Stock & Load.
                </AlertDescription>
              </Alert>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActiveDialog(false)}>
                Close
              </Button>
              <Button variant="destructive" onClick={handleStop}>
                <X className="h-4 w-4 mr-2" />
                Stop Sharing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  // Consent dialog for pending session
  return (
    <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-blue-600" />
            Support Request
          </DialogTitle>
          <DialogDescription>
            A support admin would like to view your screen to help troubleshoot an issue.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Session info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>What will be shared:</strong>
            </p>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li>Only your Stock & Load browser tab</li>
              <li>View-only access (no control of your device)</li>
              <li>Session expires in 15 minutes</li>
            </ul>
          </div>
          
          {/* Privacy notice */}
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              <strong>Privacy:</strong> The admin will NOT be able to see other tabs, 
              windows, or anything outside this application.
            </AlertDescription>
          </Alert>
          
          {/* Error message */}
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={handleDecline}
            disabled={loading}
          >
            Decline
          </Button>
          <Button 
            onClick={handleAccept}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Accept & Share Tab
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupportSessionBanner;
